import { useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import { type Flow, type HandoverSpec } from '@/designs-dev/handover/types';
import Note from '@/designs-dev/annotations/Note';
import { homeFirstVisitSpec } from '@/karehero-app/handover/home-first-visit';
import { homeNextSpec } from '@/karehero-app/handover/home-next';
import { DEFAULT_ITEMS } from '@/designs-dev/device/TabBar';
import ActionCard from '@/components/wrapped/ActionCard';
import { ActionPerson } from '@/karehero-app/actions/ActionDetail';
import CareHubScreen, {
  HOME_TABS,
  type HubNudge,
} from '@/karehero-app/care/CareHubScreen';
import { CARE_HUB, WHY } from '@/karehero-app/care/CareHub.content';
import {
  actionPerson,
  EVERYONE_ACTIONS,
} from '@/karehero-app/care/CareHub.fixtures';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import { Bolded } from '@/karehero-app/tell-us-once/walkthrough/OnboardingWalkthrough';
import AppTab from '@/karehero-app/shell/AppTab';
import ActionsScreen from '@/karehero-app/actions/ActionsScreen';
import { type HubShortlistsInitial } from '@/karehero-app/care/HubShortlists';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import StatusBar from '@/designs-dev/device/StatusBar';

const noop = () => {};

// The reasoning under a step, for review: an annotation, the way the other
// flows pages explain a step, with the points as a list inside it.
const why = (points: string[]) => (
  <Note title='Readme' variant='body2'>
    <Box component='ul' sx={{ m: 0, pl: 2, '& li + li': { mt: 0.5 } }}>
      {points.map((p) => (
        <li key={p}>
          <Bolded text={p} />
        </li>
      ))}
    </Box>
  </Note>
);

type Hub = Parameters<typeof CareHubScreen>[0];

// The other things that could lead on Home, in the order they come up.
const LEADS: [string, HubNudge][] = [
  ['Financial status', 'finances'],
  ['A call, after finances', 'financesCall'],
  ['Care assessment', 'assessment'],
  ['A call, after the assessment', 'assessmentCall'],
  ['Power of attorney', 'poa'],
  ['A benefit, the biggest first', 'benefit'],
];
const hub =
  (props: Hub = {}) =>
  () => <CareHubScreen {...props} />;
// For a screen that's already in the app: a stand-in for it here, not the
// design.
const current = () => (
  <ScreenRoot>
    <StatusBar />
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant='body1' sx={{ color: 'text.secondary' }}>
        Current screen placeholder
      </Typography>
    </Box>
  </ScreenRoot>
);

// Arranging care's pages, pushed over it.
const arranging = (initialShortlists?: HubShortlistsInitial) =>
  hub({
    grid: true,
    tidy: true,
    subjects: true,
    initialArea: 'arranging',
    initialShortlistsOpen: true,
    initialShortlists,
  });

// Money after the check, as Joining up has it.
const money = (props: Hub) =>
  hub({
    grid: true,
    tidy: true,
    subjects: true,
    initialArea: 'money',
    ...props,
  });

// An older page's flows, shut, each on its own row and named for the page.
const folded = (spec: HandoverSpec, page: string): Flow[] =>
  spec.flows.map((flow) => ({
    ...flow,
    title: `${page}: ${flow.title}`,
    collapsed: true,
    beside: false,
    under: false,
    alongside: false,
  }));

// The hub opened on its Help tab.
const help = (props: Hub = {}) =>
  hub({ grid: true, tidy: true, subjects: true, initialTab: 'help', ...props });

// Concept 2's Home: everyone at once, each action tagged with who it is about,
// so Home never needs to know who is picked.
const everyone = () => (
  <AppTab
    active='home'
    items={DEFAULT_ITEMS.filter((t) => t.id !== 'profile')}
    tone='neutral'
  >
    <HomeScreen
      lead={
        <Stack spacing={1.5}>
          <Typography variant='h4'>{CARE_HUB.todo}</Typography>
          {EVERYONE_ACTIONS.map((a) => (
            <ActionCard key={a.id} action={a} onToggle={noop} onOpen={noop} />
          ))}
        </Stack>
      }
      cards={null}
      onNotifications={noop}
    />
  </AppTab>
);

// The Actions tab as the hub has it: its bar, each action by person, and
// Create action in the tab bar's fade rather than a footer of its own.
const HubActions = () => {
  const [creating, setCreating] = useState(false);
  return (
    <ActionPerson.Provider value={actionPerson}>
      <AppTab
        active='actions'
        items={HOME_TABS}
        tone='neutral'
        action={
          <Button
            variant='primary'
            size='md'
            iconLeft='plus'
            ariaLabel='Create action'
            onPress={() => setCreating(true)}
          >
            Create action
          </Button>
        }
      >
        {/* No name in the bar, the same as the hub's own. */}
        <ActionsScreen
          onNotifications={noop}
          create='none'
          createOpen={creating}
          onCreateOpenChange={setCreating}
        />
      </AppTab>
    </ActionPerson.Provider>
  );
};

/**
 * Care as the hub for one person, with Profile folded into it: no toggles, one
 * scroll. Every frame is the same screen opened in a different place.
 */
export const careHubSpec: HandoverSpec = {
  id: 'joining-up',
  title: 'Joining up',
  description: '',
  canvas: true,
  links: [],
  states: [],
  components: {},
  flows: [
    {
      title: 'Care is Home, by subject',
      present: '/tell-us-once/joining-up-walkthrough',
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'c4-first',
          title: 'Just them, no circle yet',
          note: why(WHY.first),
          render: hub({
            grid: true,
            tidy: true,
            subjects: true,
            solo: true,
            emptyCircle: true,
          }),
        },
        {
          id: 'c4-home',
          title: 'As their circle grows',
          note: why(WHY.home),
          render: hub({ grid: true, tidy: true, subjects: true }),
        },
        {
          id: 'c4-before',
          title: 'Before the check',
          note: why(WHY.before),
          render: hub({
            grid: true,
            tidy: true,
            subjects: true,
            initialPerson: 'Brian',
            nudge: 'check',
          }),
          // What else could lead, on Margaret's Home: alike enough to step
          // through in one frame rather than a frame each.
          variants: LEADS.map(([title, nudge]) => ({
            title,
            render: hub({ grid: true, tidy: true, subjects: true, nudge }),
          })),
        },
        {
          id: 'c4-before-money',
          title: 'Money, before the check',
          note: why(WHY.moneyBefore),
          render: hub({
            grid: true,
            tidy: true,
            subjects: true,
            initialPerson: 'Brian',
            initialArea: 'money',
          }),
        },
        {
          id: 'c4-money',
          title: 'Money, after the check',
          note: why(WHY.moneyAfter),
          render: hub({
            grid: true,
            tidy: true,
            subjects: true,
            initialArea: 'money',
          }),
          alt: [
            {
              title: 'View other benefits',
              render: hub({
                grid: true,
                tidy: true,
                subjects: true,
                initialArea: 'money',
                initialOthers: true,
              }),
            },
            {
              title: 'Arriving from the end of the calculator',
              render: hub({
                grid: true,
                tidy: true,
                subjects: true,
                initialArea: 'money',
                arrived: true,
              }),
            },
          ],
        },
        {
          id: 'c4-arranging',
          title: 'Arranging care',
          note: why(WHY.arranging),
          render: hub({
            grid: true,
            tidy: true,
            subjects: true,
            initialArea: 'arranging',
          }),
        },
        {
          id: 'c4-legal',
          title: 'Legal',
          note: why(WHY.legal),
          render: hub({
            grid: true,
            tidy: true,
            subjects: true,
            initialArea: 'legal',
          }),
          alt: {
            title: 'If they have it',
            render: hub({
              grid: true,
              tidy: true,
              subjects: true,
              initialArea: 'legal',
              hasPoa: true,
            }),
          },
        },
        {
          id: 'c4-record',
          title: 'About them',
          note: why(WHY.record),
          render: hub({
            grid: true,
            tidy: true,
            subjects: true,
            initialArea: 'record',
          }),
        },
      ],
    },
    // Under the hub: coming back to the check once it's done.
    {
      title: 'Coming back to the check',
      under: true,
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'recheck-answers',
          title: "Their answers, under the form's name",
          render: money({ initialRecheck: true }),
        },
        {
          id: 'recheck-change',
          title: 'Changing one',
          render: money({
            initialRecheck: true,
            initialRecheckEditing: 'hours',
          }),
        },
        {
          id: 'recheck-done',
          title: 'Checked again, what is new',
          render: money({ rechecked: true }),
        },
      ],
    },
    {
      // Beside the hub, since it's a tab of its own rather than part of it.
      title: 'Help',
      beside: true,
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'help',
          title: 'Help',
          render: hub({
            grid: true,
            tidy: true,
            subjects: true,
            initialTab: 'help',
          }),
        },
        {
          id: 'help-webinars',
          title: 'Every webinar',
          render: help({ initialHelpList: 'webinars' }),
        },
        {
          id: 'help-webinar',
          title: 'A webinar',
          render: current,
        },
        {
          id: 'help-articles',
          title: 'Every article',
          render: help({ initialHelpList: 'articles' }),
        },
        {
          id: 'help-article',
          title: 'An article',
          render: current,
        },
        {
          id: 'help-chat',
          title: 'Talk to us (Intercom)',
          render: current,
        },
      ],
    },
    // The Actions tab, from the Actions designs, stacked under Help: the other
    // tab beside the hub, with the hub's tab bar. Just the list for now; the
    // rest of the Actions flows are on the Actions page.
    {
      title: 'Actions',
      under: true,
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'actions',
          title: 'Actions',
          render: () => <HubActions />,
        },
      ],
    },
    // Beside Actions: where Arranging care's Shortlists row leads. Pushed
    // pages, so a back where the bar was.
    {
      title: 'Shortlists',
      alongside: true,
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'shortlists',
          title: 'Shortlists',
          render: arranging(),
        },
        {
          id: 'shortlist',
          title: 'A shortlist',
          render: arranging({
            shortlistId: 'residential-nursing',
          }),
        },
        {
          id: 'provider',
          title: 'A provider',
          render: arranging({
            shortlistId: 'residential-nursing',
            providerId: 'oakwood',
          }),
        },
        {
          id: 'book-visit',
          title: 'Book a visit',
          render: arranging({
            shortlistId: 'residential-nursing',
            providerId: 'oakwood',
            booking: true,
          }),
        },
      ],
    },
    {
      title: 'Care is Home, a quieter card',
      collapsed: true,
      divider: true,
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'c3-tidy',
          title: 'After the check',
          render: hub({ grid: true, tidy: true }),
        },
        {
          id: 'c3-tidy-before',
          title: 'Before the check',
          render: hub({ grid: true, tidy: true, initialPerson: 'Brian' }),
        },
        {
          id: 'c3-tidy-could',
          title: 'What they could get',
          render: hub({ grid: true, tidy: true, initialArea: 'could' }),
        },
        {
          id: 'c3-tidy-forms',
          title: 'Forms',
          render: hub({ grid: true, tidy: true, initialArea: 'forms' }),
        },
        {
          id: 'c3-tidy-arranging',
          title: 'Arranging care',
          render: hub({ grid: true, tidy: true, initialArea: 'arranging' }),
        },
        {
          id: 'c3-tidy-record',
          title: 'About them',
          render: hub({ grid: true, tidy: true, initialArea: 'record' }),
        },
      ],
    },
    {
      title: 'Care is Home: a card per area',
      collapsed: true,
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'c3-home',
          title: 'After the check',
          render: hub({ grid: true }),
        },
        {
          id: 'c3-before',
          title: 'Before the check',
          render: hub({ grid: true, initialPerson: 'Brian' }),
        },
        {
          id: 'c3-could',
          title: 'What they could get',
          render: hub({ grid: true, initialArea: 'could' }),
        },
        {
          id: 'c3-forms',
          title: 'Forms',
          render: hub({ grid: true, initialArea: 'forms' }),
        },
        {
          id: 'c3-arranging',
          title: 'Arranging care',
          render: hub({ grid: true, initialArea: 'arranging' }),
        },
        {
          id: 'c3-record',
          title: 'About them',
          render: hub({ grid: true, initialArea: 'record' }),
        },
      ],
    },
    {
      title: 'Care is Home, with the people as squares',
      collapsed: true,
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'c3-squares',
          title: 'After the check',
          render: hub({ grid: true, squares: true }),
        },
        {
          id: 'c3-squares-before',
          title: 'Before the check',
          render: hub({ grid: true, squares: true, initialPerson: 'Brian' }),
        },
      ],
    },
    {
      title: 'Split: Home for everyone, Care for one',
      collapsed: true,
      arrows: false,
      numbered: false,
      steps: [
        { id: 'c2-home', title: 'Home, everyone', render: everyone },
        {
          id: 'c2-care',
          title: 'Care, one person',
          render: hub({ merged: true }),
        },
        {
          id: 'c2-lower',
          title: 'Further down',
          render: hub({ merged: true, initialSection: 'arranging' }),
        },
        {
          id: 'c2-before',
          title: 'Before the check',
          render: hub({ merged: true, initialPerson: 'Brian' }),
        },
      ],
    },
    {
      title: 'All on Care',
      collapsed: true,
      arrows: false,
      numbered: false,
      steps: [
        { id: 'top', title: 'After the check', render: hub() },
        {
          id: 'forms',
          title: 'Forms, then arranging care',
          render: hub({ initialSection: 'forms' }),
        },
        {
          id: 'record',
          title: 'Their record, last',
          render: hub({ initialSection: 'record' }),
        },
        {
          id: 'before',
          title: 'Before the check',
          render: hub({ initialPerson: 'Brian' }),
        },
      ],
    },
    {
      title: 'From the hub',
      collapsed: true,
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'claim',
          title: 'A result',
          render: hub({ openClaim: 'Attendance Allowance' }),
        },
        {
          id: 'about',
          title: 'A record page',
          render: hub({ initialPage: 'profile' }),
        },
        {
          id: 'circle',
          title: 'The care circle',
          render: hub({ initialCircle: 'manage' }),
        },
      ],
    },
    // The two Home pages Joining up grew out of, kept here so the history is
    // in one place. Each flow shut, on a row of its own, under its page's name.
    ...folded(homeFirstVisitSpec, 'Home, first visit'),
    ...folded(homeNextSpec, 'Home'),
  ],
};

// The ways of trying Home picked for feedback on 4 November, by the hub's
// props. Every one has a b underneath:
// the same Home with a nudge, so the person card is seen with the thing to do
// next under it.
const HOME_VERSIONS: [string, string, Hub][] = [
  ['home-now', 'As it is in Joining up', {}],
  [
    'home-now-untitled',
    'As it is, without Who you care for',
    { untitled: true, careForLine: true },
  ],
  [
    'home-others-smaller',
    'Others smaller, underneath',
    { summaryAbout: true, hideSummary: true, quietOthers: true },
  ],
  [
    'home-card-menu',
    'The card opens care circle and About',
    { hideSummary: true, cardMenu: true, untitled: true, switchMotion: true },
  ],
];

// A version as a step: the Home, and the same Home with a nudge as its b.
const homeStep = ([id, title, props]: [string, string, Hub]) => {
  const base = { grid: true, tidy: true, subjects: true, ...props };
  return {
    id,
    title,
    render: hub(base),
    alt: {
      title: 'With a nudge',
      render: hub({ ...base, nudge: 'finances' as const }),
    },
  };
};

// The far left and the far right, picked on 4 November.
const FAVOURITES = ['home-now', 'home-card-menu'];

/**
 * Home on its own, from the dynamic flows catch up on 3 November: Joining up's
 * Home, then each way of trying it, side by side to compare.
 */
export const homePageSpec: HandoverSpec = {
  id: 'home-page',
  title: 'Home page',
  description: '',
  canvas: true,
  links: [],
  states: [],
  components: {},
  flows: [
    {
      title: 'Variants',
      arrows: false,
      numbered: false,
      steps: HOME_VERSIONS.map(homeStep),
    },
    // The two picked from the row, beside it, to try colours on.
    {
      title: 'Colour play',
      beside: true,
      arrows: false,
      numbered: false,
      steps: HOME_VERSIONS.filter(([id]) => FAVOURITES.includes(id)).flatMap(
        ([id, title, props]) => [
          homeStep([`fav-${id}`, title, props]),
          // Floral White page, Gardenia card, pastel tiles.
          homeStep([
            `fav-${id}-pastel`,
            `${title}, pastels`,
            { ...props, pastel: true },
          ]),
          // The same page, but the pastels only on the icon discs, so the
          // nudge is still the one strong colour.
          homeStep([
            `fav-${id}-pastel-dots`,
            `${title}, pastel icons`,
            { ...props, pastelDots: true },
          ]),
          // The Gardenia page as it is, with the pastels on the icon discs.
          homeStep([
            `fav-${id}-icon-colours`,
            `${title}, Gardenia with pastel icons`,
            { ...props, iconColours: true },
          ]),
        ],
      ),
    },
  ],
};
